import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listMyCompanies,
  listGlobalCompanies,
  getCompanyFacets,
  getMyCompany,
  updateMyCompany,
  enrichMyCompany,
  createCompany,
} from "@/api/companies";
import type { CompanyInput } from "@career-ops-amplify/shared";

export {
  useContacts,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
  useBatchCreateContacts,
  useLogContactInteraction,
} from "./useContacts";

const COMPANIES_QUERY_KEY = ["companies"] as const;

export function useMyCompanies() {
  return useQuery({
    queryKey: [...COMPANIES_QUERY_KEY, "my"],
    queryFn: listMyCompanies,
    staleTime: 30_000,
  });
}

export function useGlobalCompanies(filters?: {
  industry?: string;
  country?: string;
}) {
  return useQuery({
    queryKey: [...COMPANIES_QUERY_KEY, "global", filters ?? {}],
    queryFn: () => listGlobalCompanies(filters),
    staleTime: 60_000,
  });
}

export function useCompanyFacets() {
  return useQuery({
    queryKey: [...COMPANIES_QUERY_KEY, "facets"],
    queryFn: getCompanyFacets,
    staleTime: 120_000,
  });
}

/** @deprecated Use useMyCompanies */
export function useCompanies() {
  return useMyCompanies();
}

export function useMyCompany(slug: string | undefined) {
  return useQuery({
    queryKey: [...COMPANIES_QUERY_KEY, "detail", slug],
    queryFn: () => getMyCompany(slug!),
    enabled: !!slug,
    staleTime: 30_000,
  });
}

/** @deprecated Use useMyCompany */
export function useCompany(slug: string | undefined) {
  return useMyCompany(slug);
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CompanyInput) => createCompany(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
    },
  });
}

export function useUpdateMyCompany(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: Record<string, unknown>) =>
      updateMyCompany(slug, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
    },
  });
}

export function useEnrichMyCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => enrichMyCompany(slug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
    },
  });
}

/** @deprecated Use useEnrichMyCompany */
export function useResearchMyCompany() {
  return useEnrichMyCompany();
}

/** @deprecated Use useEnrichMyCompany */
export function useResearchCompany() {
  return useEnrichMyCompany();
}
